import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { stockAdjustmentPath } from '../../lib/slug';
import AttachmentsCard from '../attachments/AttachmentsCard';
import { adjustmentKindMeta, adjustmentReasonLabel, adjustmentStatusMeta, adjustmentsApi } from './adjustmentsApi';
import '../requisitions/requisitions.css';
import '../transfers/transfers.css';

const lineValue = (l) => (l.unitCost ? `${l.unitCost.currency} ${formatAmount(Number(l.unitCost.amount) * Math.abs(Number(l.quantity)))}` : '—');

export default function AdjustmentDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { hasPermission } = useProperty();
  const canAdjust = hasPermission('inventory.adjust');

  const [adjustment, setAdjustment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAdjustment(await adjustmentsApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Adjustment not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!adjustment?.id) return;
    const canonical = stockAdjustmentPath(adjustment);
    if (`/adjustments/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [adjustment, routeRef, navigate]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name || '—';

  const doPost = async () => {
    setBusy(true);
    try {
      const posted = await adjustmentsApi.post(adjustment.id);
      setPostOpen(false);
      setAdjustment(posted);
      toast.success(t('Adjustment {number} posted', { number: posted.number }));
    } catch (err) {
      toast.error(t('Could not post adjustment'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await adjustmentsApi.cancel(adjustment.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setAdjustment(cancelled);
      toast.success(adjustment.status === 'Posted' ? t('Adjustment cancelled and reversed') : t('Draft adjustment discarded'));
    } catch (err) {
      toast.error(t('Could not cancel adjustment'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !adjustment) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load adjustment")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/adjustments')}>{t('Back to adjustments')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = adjustmentStatusMeta(adjustment.status);
  const kindMeta = adjustmentKindMeta(adjustment.kind);
  const isDraft = adjustment.status === 'Draft';
  const isPosted = adjustment.status === 'Posted';
  const isCancelled = adjustment.status === 'Cancelled';

  const headerActions = canAdjust && !isCancelled ? (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {isDraft && (
        <Button variant="primary" size="sm" leadingIcon={<Icon.Check size={14} />} onClick={() => setPostOpen(true)}>{t('Post adjustment')}</Button>
      )}
      <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>
        {isPosted ? t('Cancel & reverse') : t('Discard draft')}
      </Button>
    </div>
  ) : null;

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    { term: t('Kind'), description: <Badge tone={kindMeta.tone}>{t(kindMeta.label)}</Badge> },
    { term: t('Location'), description: locationName(adjustment.adjustmentLocationId) },
    { term: t('Adjustment date'), description: adjustment.adjustmentDate ? new Date(adjustment.adjustmentDate).toLocaleDateString() : '—' },
    { term: isPosted ? t('Posted by') : t('Created by'), description: (isPosted ? adjustment.postedByName : adjustment.createdByName) || '—' },
    { term: isPosted ? t('Posted at') : t('Created at'), description: new Date(isPosted ? adjustment.postedAt : adjustment.createdAt).toLocaleString() },
  ];

  const renderLine = (l) => {
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    return (
      <div className="grn-line" key={l.lineId}>
        <div className="grn-line__item">
          <div className="grn-line__name">{l.description}</div>
          {l.sku && <div className="grn-line__sub">{l.sku}</div>}
        </div>
        <div className="grn-line__qty">
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Quantity')}</span>
            <span className="grn-line__stat-value">{l.quantity}{uom}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Reason')}</span>
            <span className="grn-line__stat-value">{t(adjustmentReasonLabel(l.reason))}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Unit cost')}</span>
            <span className="grn-line__stat-value">{money(l.unitCost)}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Value')}</span>
            <span className="grn-line__stat-value">{lineValue(l)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/adjustments')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Adjustments')}
      </Button>

      <PageHeader
        title={isDraft ? t('Draft adjustment') : t('Adjustment {number}', { number: adjustment.number })}
        description={<span className="req-detail__number">{t(kindMeta.label)} · {locationName(adjustment.adjustmentLocationId)}</span>}
        icon={<Icon.TrendingDown size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This adjustment is a draft — no stock has changed yet. Attach photo evidence below, then post it to commit the change to the ledger.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This adjustment was cancelled')}{adjustment.cancelledByName ? ` ${t('by {name}', { name: adjustment.cancelledByName })}` : ''}{adjustment.cancelledAt ? ` ${t('on {date}', { date: new Date(adjustment.cancelledAt).toLocaleString() })}` : ''}.{adjustment.cancelledReason ? ` ${t('Reason: {reason}', { reason: adjustment.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Adjusted value')} value={money(adjustment.total)} />
        <KpiTile label={t('Items')} value={adjustment.lineCount} />
        <KpiTile label={t('Kind')} value={t(kindMeta.label)} />
        <KpiTile label={t('Location')} value={locationName(adjustment.adjustmentLocationId)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {adjustment.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{adjustment.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header>
            <Card.Title>{t('Items')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {isPosted
                ? t('These quantities were applied to the stock ledger.')
                : t('These quantities will be applied when the adjustment is posted.')}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="grn-lines">
              {(adjustment.lines || []).map(renderLine)}
            </div>
          </Card.Body>
        </Card>

        <AttachmentsCard basePath={`/stock-adjustments/${adjustment.id}/attachments`} canManage={canAdjust && isDraft} />
      </div>

      <ConfirmDialog
        open={postOpen}
        title={t('Post adjustment')}
        description={t('Posting commits the change to the stock ledger and updates on-hand at the location. You can still reverse it afterwards.')}
        confirmLabel={t('Post adjustment')}
        tone="primary"
        loading={busy}
        onConfirm={doPost}
        onCancel={() => (busy ? null : setPostOpen(false))}
      />

      <ConfirmDialog
        open={cancelOpen}
        title={isPosted ? t('Cancel & reverse adjustment') : t('Discard draft adjustment')}
        description={isPosted
          ? t('This reverses the adjustment back out of the stock ledger and restores the on-hand balances.')
          : t('This voids the draft adjustment. Nothing has been committed, so there is nothing to reverse.')}
        confirmLabel={isPosted ? t('Cancel adjustment') : t('Discard draft')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t('Reason (optional)')} />
      </ConfirmDialog>
    </PageContainer>
  );
}
